import { assertNotEmpty } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useIsErrorForField } from '../../hooks/useIsErrorForField'
import type {
  DynamicConfigurationTemplateFunctionValue,
  FPlusExpressionValue,
} from '../../types/templateModel'
import { useOperationsContext } from '../../workspace/operationsContext'
import { InheritanceIndicator } from '../inheritanceIndicator/InheritanceIndicator'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'
import {
  functionExpressionPath,
  functionTitle,
} from '../templateFieldsTable/fieldsHelpers'

type Props = {
  value: DynamicConfigurationTemplateFunctionValue
  onChange?: (nextValue: FPlusExpressionValue) => Promise<void>
  onRemove?: () => void | Promise<void>
  isShowingTitle: boolean
  isShowingErrors: boolean
}

export const SimpleFieldValueDynamicFunction = ({
  value,
  onChange,
  onRemove,
  isShowingTitle,
  isShowingErrors,
}: Props) => {
  const translate = useTranslate()
  const title = useMemo(() => functionTitle(value, ''), [value])
  const { isDraftTemplate } = useTemplateContext()
  const { errors } = useOperationsContext()
  const isErrorForField = useIsErrorForField()
  const functionErrors = useMemo(
    () => errors.filter(({ error }) => isErrorForField(value, error)),
    [errors, isErrorForField, value],
  )
  const handleChange = useCallback(
    (nextValue: FPlusExpressionValue) => {
      assertNotEmpty(onChange)

      return onChange(nextValue)
    },
    [onChange],
  )

  return (
    <div>
      <div className="flex w-full items-center justify-between text-sm text-gray-300">
        <InheritanceIndicator value={value} />
        {isShowingTitle && (
          <>
            <h4>{title}</h4>
            {isDraftTemplate && onRemove && (
              <IconButton
                onClick={onRemove}
                tip={translate('action.remove')}
                iconName="MinusCircle"
              />
            )}
          </>
        )}
      </div>
      <TemplateFieldExpression
        isExpanded
        expression={value.function}
        onChange={handleChange}
        path={functionExpressionPath(value.path)}
        fallbackCode=""
        fromOrigin={value.origin}
      />
      {isShowingErrors &&
        (functionErrors ?? []).map(({ error }) => (
          <div className="text-error-500" key={error.errorMessage}>
            {error.errorMessage}
          </div>
        ))}
    </div>
  )
}
